import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Alert,
  Avatar,
  Box,
  Button,
  FormControlLabel,
  Grid2 as Grid,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { archiveProduct, listCompanies, readProduct, restoreProduct } from '../../api/business/endpoints';
import type { StockOperation, Batch } from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { usePermissions } from '../../auth/usePermissions';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { missingLanguages, textsOf } from '../../components/localisedText';
import { formatDate, formatMoney, formatNumber } from '../../components/format';
import { PageHeader } from '../../components/PageHeader';
import { SortableCell } from '../../components/SortableCell';
import { StatusChip } from '../../components/StatusChip';
import { TableFilters } from '../../components/TableFilters';
import { useContentLanguages } from '../../components/useContentLanguages';
import { sortRows, useSort } from '../../components/useSort';
import { isVariable, variantLabel } from '../../components/variants';
import { useLanguage } from '../../i18n/LanguageContext';

import { ProductAvailabilitySection } from './ProductAvailabilitySection';
import { ProductAxesDialog } from './ProductAxesDialog';
import { ProductClassificationSection } from './ProductClassificationSection';
import { ProductCustomizationSection } from './ProductCustomizationSection';
import { ProductDialog } from './ProductDialog';
import { ProductGallerySection } from './ProductGallerySection';
import { ProductFilesSection } from './ProductFilesSection';
import { ProductLegalSection } from './ProductLegalSection';
import { ProductPackSection } from './ProductPackSection';
import { ProductPropertiesSection } from './ProductPropertiesSection';
import { ProductRelationsSection } from './ProductRelationsSection';
import { ProductShopsSection } from './ProductShopsSection';
import { ProductSeoSection } from './ProductSeoSection';
import { ProductVariantsSection } from './ProductVariantsSection';
import { ShopOrdersTable } from './ShopOrdersTable';
import { StockOperationDialog } from './StockOperationDialog';
import type { StockOperationKind } from './StockOperationKind';
import { BatchDialog } from './BatchDialog';

type BatchSortKey = 'variant' | 'expiryDate' | 'lotNumber' | 'effectivePrice' | 'onHand';

type Dialog =
  | { kind: 'product' }
  | { kind: 'axes' }
  | { kind: 'batch'; batch: Batch | null }
  | { kind: 'stock'; operation: StockOperationKind; batchId?: string };

/**
 * One product: what a shop shows of it (R0030 WP-05), its legal, availability and SEO properties (WP-07), its
 * related products (WP-08), its variants and its batches — each with its own expiry, lot,
 * price and stock (US-08, US-09). A pack (WP-09) shows its contents instead of batches: it holds no stock, so it is
 * never received; selling it issues its items. A virtual product (WP-10) has its downloads; any product attachments
 * and the fields a customer fills in when ordering it (WP-11).
 */
export const ProductDetailPage = () => {
  const { companyId = '', productId = '' } = useParams();
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const query = useQuery({
    queryKey: ['product', companyId, productId],
    queryFn: () => readProduct(companyId, productId),
  });
  const companies = useQuery({ queryKey: ['companies'], queryFn: listCompanies });
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [done, setDone] = useState<StockOperation | null>(null);
  const [lot, setLot] = useState('');
  const [onlyInStock, setOnlyInStock] = useState(false);
  const [variantFilter, setVariantFilter] = useState('');
  const sort = useSort<BatchSortKey>('expiryDate');
  const { languages } = useContentLanguages();
  const archive = useAppMutation(
    () => archiveProduct(companyId, productId),
    ['product', 'products', 'licence-usage'],
  );
  const restore = useAppMutation(
    () => restoreProduct(companyId, productId),
    ['product', 'products', 'licence-usage'],
  );

  const product = query.data;
  const currency = companies.data?.find((company) => company.id === companyId)?.currency ?? 'EUR';
  const writable = permissions.writable && product?.status === 'ACTIVE';
  const can = (code: string): boolean => permissions.can(code, companyId);
  const canManage = permissions.writable && can('products.manage');

  if (query.isLoading) {
    return <Skeleton variant="rounded" height={320} />;
  }

  if (!product) {
    return (
      <>
        <ErrorNotice error={query.error} onRetry={() => void query.refetch()} />
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/products')}>
          {t('products.back')}
        </Button>
      </>
    );
  }

  const variable = isVariable(product);
  const pack = product.type === 'PACK';
  const missingNames = missingLanguages(textsOf(product.translations, 'name', languages), languages);
  const labelOf = new Map(product.variants.map((variant) => [variant.id, variantLabel(variant, t)]));
  const valueOf: Record<BatchSortKey, (batch: Batch) => string | number | null> = {
    variant: (batch) => labelOf.get(batch.variantId) ?? null,
    expiryDate: (batch) => batch.expiryDate,
    lotNumber: (batch) => batch.lotNumber,
    effectivePrice: (batch) => batch.effectivePriceMinor,
    onHand: (batch) => batch.onHand,
  };
  const needle = lot.trim().toLocaleLowerCase('sk');
  const batches = sortRows(
    product.batches.filter(
      (batch) =>
        (!onlyInStock || batch.onHand > 0) &&
        (variantFilter === '' || batch.variantId === variantFilter) &&
        (needle === '' || (batch.lotNumber ?? '').toLocaleLowerCase('sk').includes(needle)),
    ),
    sort.direction,
    valueOf[sort.key],
  );

  const openStock = (operation: StockOperationKind, batchId?: string): void => {
    setDone(null);
    setDialog({ kind: 'stock', operation, batchId });
  };

  return (
    <>
      <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate('/products')} sx={{ mb: 1 }}>
        {t('products.back')}
      </Button>
      <PageHeader
        title={product.name}
        media={
          product.mainImage ? (
            <Avatar
              variant="rounded"
              src={product.mainImage.urls.thumb.webp}
              alt={product.name}
              sx={{ width: 64, height: 64 }}
            />
          ) : undefined
        }
        subtitle={[product.ean, product.sku].filter(Boolean).join(' · ') || undefined}
        action={
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {writable && can('stock.receive') && !pack ? (
              <Button variant="contained" onClick={() => openStock('receipt')}>
                {t('stock.receipt')}
              </Button>
            ) : null}
            {writable && can('stock.issue') && (!pack || product.packItems.length > 0) ? (
              <Button variant={pack ? 'contained' : 'outlined'} onClick={() => openStock('issue')}>
                {t('stock.issue')}
              </Button>
            ) : null}
            {can('stock.read') ? (
              <Button onClick={() => navigate(`/movements?productId=${product.id}`)}>
                {t('nav.movements')}
              </Button>
            ) : null}
            {canManage && product.status === 'ACTIVE' && !variable && !pack ? (
              <Button onClick={() => setDialog({ kind: 'axes' })}>{t('variants.enable')}</Button>
            ) : null}
            {canManage ? (
              <>
                <Button onClick={() => setDialog({ kind: 'product' })}>{t('common.edit')}</Button>
                {product.status === 'ACTIVE' ? (
                  <Button color="warning" onClick={() => archive.mutate(undefined)}>
                    {t('common.archive')}
                  </Button>
                ) : (
                  <Button onClick={() => restore.mutate(undefined)}>{t('common.restore')}</Button>
                )}
              </>
            ) : null}
          </Stack>
        }
      />
      {missingNames.length > 0 ? (
        <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
          {t('languages.productMissing', {
            codes: missingNames.map((code) => code.toUpperCase()).join(', '),
          })}
        </Alert>
      ) : null}
      <ErrorNotice error={archive.error ?? restore.error} />
      {done ? (
        <Alert severity="success" variant="outlined" sx={{ mb: 2.5 }} onClose={() => setDone(null)}>
          {done.replayed ? t('stock.replayed') : t('stock.done', { count: done.movements.length })}
        </Alert>
      ) : null}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[
          pack
            ? {
                label: t('products.packsAvailable'),
                value: formatNumber(product.availableQuantity ?? 0, language),
              }
            : {
                label: t('products.onHand'),
                value:
                  product.availableQuantity === null
                    ? t('products.alwaysAvailable')
                    : formatNumber(product.totalOnHand, language),
              },
          pack
            ? { label: t('products.packItemCount'), value: formatNumber(product.packItems.length, language) }
            : { label: t('products.batches'), value: formatNumber(product.batchCount, language) },
          {
            label: product.saleActive
              ? `${t('products.priceNow')} · ${t('products.onSale')}`
              : t('products.price'),
            value: formatMoney(product.effectivePriceMinor, currency, language),
          },
        ].map((tile) => (
          <Grid key={tile.label} size={{ xs: 12, sm: 4 }}>
            <Paper variant="outlined" sx={{ p: 2.5 }}>
              <Typography variant="body2" color="text.secondary">
                {tile.label}
              </Typography>
              <Typography variant="h5" sx={{ mt: 0.5 }}>
                {tile.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {pack ? (
        <ProductPackSection
          companyId={companyId}
          product={product}
          canManage={canManage && product.status === 'ACTIVE'}
        />
      ) : null}

      <ProductPropertiesSection product={product} currency={currency} />

      <ProductLegalSection product={product} currency={currency} />

      <ProductAvailabilitySection product={product} />

      <ProductSeoSection product={product} />

      <ProductClassificationSection product={product} />

      <ProductRelationsSection
        companyId={companyId}
        product={product}
        canManage={canManage && product.status === 'ACTIVE'}
      />

      {product.type === 'VIRTUAL' ? (
        <ProductFilesSection
          companyId={companyId}
          product={product}
          kind="downloads"
          canManage={canManage && product.status === 'ACTIVE'}
        />
      ) : null}

      {permissions.can('integrations.read', companyId) ? (
        <ProductShopsSection companyId={companyId} productId={product.id} />
      ) : null}

      <ProductCustomizationSection
        companyId={companyId}
        product={product}
        canManage={canManage && product.status === 'ACTIVE'}
      />

      <ProductFilesSection
        companyId={companyId}
        product={product}
        kind="attachments"
        canManage={canManage && product.status === 'ACTIVE'}
      />

      <ProductGallerySection
        companyId={companyId}
        product={product}
        canManage={canManage && product.status === 'ACTIVE'}
      />

      {variable ? (
        <ProductVariantsSection
          companyId={companyId}
          currency={currency}
          product={product}
          canManage={canManage && product.status === 'ACTIVE'}
        />
      ) : null}

      {pack ? null : (
        <>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography variant="h6">{t('batches.title')}</Typography>
              <StatusChip status={product.status} />
            </Stack>
            {canManage && product.status === 'ACTIVE' ? (
              <Button startIcon={<AddIcon />} onClick={() => setDialog({ kind: 'batch', batch: null })}>
                {t('batches.create')}
              </Button>
            ) : null}
          </Stack>
          <Paper variant="outlined">
            <TableFilters>
              {variable ? (
                <TextField
                  select
                  size="small"
                  label={t('field.variant')}
                  value={variantFilter}
                  onChange={(e) => setVariantFilter(e.target.value)}
                  sx={{ minWidth: 180 }}
                >
                  <MenuItem value="">{t('batches.allVariants')}</MenuItem>
                  {product.variants.map((variant) => (
                    <MenuItem key={variant.id} value={variant.id}>
                      {labelOf.get(variant.id)}
                    </MenuItem>
                  ))}
                </TextField>
              ) : null}
              <TextField
                size="small"
                label={t('field.lotNumber')}
                value={lot}
                onChange={(e) => setLot(e.target.value)}
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={onlyInStock}
                    onChange={(e) => setOnlyInStock(e.target.checked)}
                  />
                }
                label={t('batches.onlyInStock')}
              />
            </TableFilters>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {variable ? (
                      <SortableCell sort={sort} column="variant">
                        {t('field.variant')}
                      </SortableCell>
                    ) : null}
                    <SortableCell sort={sort} column="expiryDate">
                      {t('field.expiryDate')}
                    </SortableCell>
                    <SortableCell sort={sort} column="lotNumber">
                      {t('field.lotNumber')}
                    </SortableCell>
                    <SortableCell sort={sort} column="effectivePrice" align="right">
                      {t('batches.effectivePrice')}
                    </SortableCell>
                    <TableCell align="right">{t('batches.purchasePrice')}</TableCell>
                    <SortableCell sort={sort} column="onHand" align="right">
                      {t('products.onHand')}
                    </SortableCell>
                    <TableCell align="right">{t('common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {batches.map((batch) => (
                    <TableRow key={batch.id} hover>
                      {variable ? (
                        <TableCell>{labelOf.get(batch.variantId) ?? t('common.none')}</TableCell>
                      ) : null}
                      <TableCell>
                        {batch.expiryDate ? formatDate(batch.expiryDate, language) : t('batches.noExpiry')}
                      </TableCell>
                      <TableCell>{batch.lotNumber ?? t('common.none')}</TableCell>
                      <TableCell align="right">
                        {formatMoney(batch.effectivePriceMinor, currency, language)}
                        {batch.priceMinor === null && batch.effectivePriceMinor !== null ? (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {t('batches.inherited')}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell align="right">
                        {formatMoney(batch.purchasePriceMinor, currency, language)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {formatNumber(batch.onHand, language)}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                          {writable && can('stock.receive') ? (
                            <Button size="small" onClick={() => openStock('receipt', batch.id)}>
                              {t('stock.receipt')}
                            </Button>
                          ) : null}
                          {writable && can('stock.issue') && batch.onHand > 0 ? (
                            <Button size="small" onClick={() => openStock('issue', batch.id)}>
                              {t('stock.issue')}
                            </Button>
                          ) : null}
                          {writable && can('stock.adjust') ? (
                            <>
                              <Button size="small" onClick={() => openStock('adjustment', batch.id)}>
                                {t('stock.adjustment')}
                              </Button>
                              {batch.onHand > 0 ? (
                                <Button
                                  size="small"
                                  color="warning"
                                  onClick={() => openStock('writeOff', batch.id)}
                                >
                                  {t('stock.writeOff')}
                                </Button>
                              ) : null}
                            </>
                          ) : null}
                          {canManage && product.status === 'ACTIVE' ? (
                            <Button size="small" onClick={() => setDialog({ kind: 'batch', batch })}>
                              {t('batches.prices')}
                            </Button>
                          ) : null}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {batches.length === 0 ? (
              <EmptyState
                title={t('batches.empty')}
                detail={writable && can('stock.receive') ? t('batches.emptyReceive') : t('batches.emptyRead')}
                action={
                  writable && can('stock.receive') ? (
                    <Button onClick={() => openStock('receipt')}>{t('stock.receipt')}</Button>
                  ) : undefined
                }
              />
            ) : null}
          </Paper>
        </>
      )}

      {can('orders.read') ? (
        <>
          <Box sx={{ mt: 3, mb: 1.5 }}>
            <Typography variant="h6">{t('products.orders')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('products.ordersHint')}
            </Typography>
          </Box>
          <ShopOrdersTable companyId={companyId} productId={product.id} />
        </>
      ) : null}

      {dialog?.kind === 'product' ? (
        <ProductDialog
          companyId={companyId}
          currency={currency}
          product={product}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === 'axes' ? (
        <ProductAxesDialog companyId={companyId} product={product} onClose={() => setDialog(null)} />
      ) : null}
      {dialog?.kind === 'batch' ? (
        <BatchDialog
          companyId={companyId}
          product={product}
          currency={currency}
          batch={dialog.batch}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === 'stock' ? (
        <StockOperationDialog
          kind={dialog.operation}
          companyId={companyId}
          currency={currency}
          product={product}
          presetBatchId={dialog.batchId}
          onClose={() => setDialog(null)}
          onDone={(operation) => {
            setDialog(null);
            setDone(operation);
          }}
        />
      ) : null}
    </>
  );
};
